import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
    LayoutDashboard, Building2, Crown, Receipt, Users,
    BarChart3, IndianRupee, Settings, Shield, CreditCard, ScrollText, FileText,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import PlatformTopBar from "@/components/platform/PlatformTopBar";
import sidebarBg from "@/assets/sidebar-bg.png";

const navGroups = [
    {
        label: "Overview",
        links: [{ to: "dashboard", label: "Dashboard", icon: LayoutDashboard }],
    },
    {
        label: "Tenants",
        links: [
            { to: "salons", label: "Salons", icon: Building2 },
            { to: "subscriptions", label: "Subscriptions", icon: CreditCard },
            { to: "users", label: "Users", icon: Users },
        ],
    },
    {
        label: "Billing",
        links: [
            { to: "plans", label: "Subscription Plans", icon: Crown },
            { to: "billing", label: "Payments", icon: Receipt },
        ],
    },
    {
        label: "Content",
        links: [{ to: "blog", label: "Blog", icon: FileText }],
    },
    {
        label: "Insights",
        links: [
            { to: "analytics", label: "Analytics", icon: BarChart3 },
            { to: "revenue", label: "Revenue", icon: IndianRupee },
        ],
    },
    {
        label: "System",
        links: [
            { to: "activity", label: "Activity Log", icon: ScrollText },
            { to: "settings", label: "Settings", icon: Settings },
        ],
    },
];

const label = "text-[15px] font-semibold text-white tracking-wide";
const labelSm = "text-xs font-medium text-white/90 tracking-wide";
const accent = "text-xs font-semibold text-[#FFE9A8] tracking-widest uppercase";
const navActive =
    "font-semibold text-[#FFF4D6] bg-[rgba(201,162,39,0.28)] border-l-[3px] border-[#D4AF37] shadow-[inset_0_0_20px_rgba(212,175,55,0.08)]";

export default function PlatformLayout() {
    const { logout } = useAuth();
    const navigate = useNavigate();

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    return (
        <div className="flex h-screen overflow-hidden bg-background">
            <aside
                className="relative h-full w-64 shrink-0 flex flex-col overflow-hidden border-r border-[#E8C547]/20 bg-cover bg-top bg-no-repeat"
                style={{ backgroundImage: `url(${sidebarBg})` }}
                data-testid="platform-sidebar"
            >
                <div
                    className="absolute inset-0 z-[1] pointer-events-none"
                    style={{
                        background: [
                            "linear-gradient(to right, rgba(48, 32, 22, 0.46) 0%, rgba(82, 62, 44, 0.24) 58%, rgba(130, 102, 72, 0.08) 100%)",
                            "linear-gradient(to bottom, rgba(42, 28, 18, 0.18) 0%, transparent 38%, transparent 72%, rgba(42, 28, 18, 0.16) 100%)",
                        ].join(", "),
                    }}
                    aria-hidden
                />

                <div className="relative z-10 flex flex-col flex-1 min-h-0 font-['Outfit',system-ui,sans-serif] antialiased">
                    <div className="px-6 py-5 border-b border-[#E8C547]/25">
                        <BrandLogo
                            size={32}
                            textClass="font-serif text-2xl font-semibold text-white"
                            accentClass={accent}
                        />
                        <p className={`mt-1 flex items-center gap-1.5 ${labelSm}`} data-testid="sidebar-platform-label">
                            <Shield className="size-3.5 text-[#FFE9A8]" />
                            Platform admin
                        </p>
                    </div>

                    <nav className="scrollbar-sidebar flex-1 overflow-y-auto px-3 py-4">
                        {navGroups.map((group) => (
                            <div key={group.label} className="mb-4 last:mb-0">
                                <p className="mb-1.5 px-3 text-[10px] font-bold uppercase tracking-[0.16em] text-white/50">
                                    {group.label}
                                </p>
                                <div className="space-y-1">
                                    {group.links.map(({ to, label: text, icon: Icon }) => (
                                        <NavLink
                                            key={to}
                                            to={to}
                                            data-testid={`platform-nav-${to}`}
                                            className={({ isActive }) =>
                                                `flex items-center gap-3 px-3 py-2.5 rounded-md transition-all duration-200 ${
                                                    isActive
                                                        ? navActive
                                                        : `${label} hover:text-[#FFE9A8] hover:bg-white/5`
                                                }`
                                            }
                                        >
                                            <Icon className="w-4 h-4 shrink-0" strokeWidth={2} />
                                            {text}
                                        </NavLink>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </nav>
                </div>
            </aside>

            <div className="scrollbar-salon flex h-full min-w-0 flex-1 flex-col overflow-y-auto bg-background">
                <PlatformTopBar onLogout={handleLogout} />
                <main className="flex-1 bg-background">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
